import { useEffect, useRef } from 'react';

/**
 * useEscapeKey — close-on-Escape for modals/overlays, stack-aware.
 *
 * Modals can stack (the codebase's "lift state up" pattern renders a
 * ConfirmationModal on top of an open BaseModal). A naive per-modal
 * `document` keydown listener would fire for EVERY open modal at once, so one
 * Escape would close the whole stack. This hook keeps a single shared listener
 * and a stack of handlers, invoking ONLY the topmost (most recently opened) one
 * — so Escape peels modals off one at a time, like users expect.
 *
 * The handler is held in a ref and the stack is keyed on `isOpen` only, so a
 * parent passing a fresh inline `onClose`/`onCancel` each render does not
 * reorder the stack (which would otherwise let a re-rendering base modal jump
 * above the confirmation sitting on top of it).
 *
 * @param {boolean}  isOpen  — whether this modal is currently open
 * @param {function} handler — called when Escape is pressed and this modal is on top
 */
const escapeStack = [];
let listenerAttached = false;

const onGlobalKeyDown = e => {
    if (e.key !== 'Escape') return;
    const top = escapeStack[escapeStack.length - 1];
    top?.current?.(e);
};

export default function useEscapeKey(isOpen, handler) {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!isOpen) return;
        escapeStack.push(handlerRef);
        if (!listenerAttached) {
            document.addEventListener('keydown', onGlobalKeyDown);
            listenerAttached = true;
        }
        return () => {
            const i = escapeStack.indexOf(handlerRef);
            if (i !== -1) escapeStack.splice(i, 1);
            if (escapeStack.length === 0 && listenerAttached) {
                document.removeEventListener('keydown', onGlobalKeyDown);
                listenerAttached = false;
            }
        };
    }, [isOpen]);
}
